import React, { useState, ChangeEvent, FormEvent, FC, useEffect } from 'react';
import { updateStatusSponsored, getOrCreateSmartAccount } from '../services/smartAccountService';
import AlertModal from '../components/AlertModal';
import EtherscanLink from '../components/EtherscanLink';
import './StatusUpdatePage.css';
import { useTranslation } from 'react-i18next';

const MAX_TEXT_LENGTH = 256;

// Match this with your SupplyChainTracker.sol ItemStatus enum
const ITEM_STATUS_OPTIONS: { nameKey: string; value: number }[] = [
    { nameKey: "statuses.0", value: 0 },
    { nameKey: "statuses.1", value: 1 },
    { nameKey: "statuses.2", value: 2 },
    { nameKey: "statuses.3", value: 3 },
    { nameKey: "statuses.4", value: 4 },
    { nameKey: "statuses.5", value: 5 },
    { nameKey: "statuses.6", value: 6 },
    { nameKey: "statuses.7", value: 7 },
    { nameKey: "statuses.8", value: 8 },
];

interface StatusHistoryEntry {
    statusName: string;
    timestamp: number;
    notes: string;
    latitude?: string;
    longitude?: string;
}

interface CertificateData {
    name: string;
    description: string;
    image: string | null;
    attributes?: { trait_type: string; value: string | number }[];
}

interface ItemData {
    id: string;
    certificate: CertificateData;
    statusHistory: StatusHistoryEntry[];
    initialCertificateURI: string;
    currentCertificateURI: string;
    historicalCertificateURIs: string[];
}

interface StatusUpdatePageProps {
    onStatusUpdated: (tokenId: string) => void;
    onBack: () => void;
    item: ItemData; // Receive full item object
}

const StatusUpdatePage: FC<StatusUpdatePageProps> = ({ onStatusUpdated, onBack, item }) => {

    const { t } = useTranslation();

    const { id: tokenId, certificate, statusHistory } = item; // Destructure for easier access

    const getCurrentStatus = () => {
        if (statusHistory && statusHistory.length > 0) {
            return statusHistory[statusHistory.length - 1];
        }
        return null;
    };

    const getNextSuggestedStatusValue = () => {
        const currentStatus = getCurrentStatus();
        if (currentStatus) {
            // Find by matching the status name with translated text
            const currentIndex = ITEM_STATUS_OPTIONS.findIndex(opt => 
                t(opt.nameKey) === currentStatus.statusName
            );
            // If current status is found and it's not the last one, suggest the next one.
            if (currentIndex > -1 && currentIndex < ITEM_STATUS_OPTIONS.length - 1) {
                return ITEM_STATUS_OPTIONS[currentIndex + 1].value;
            }
        }
        // Default to the first status if no history or if current is the last status.
        return ITEM_STATUS_OPTIONS[0].value;
    };

    const [status, setStatus] = useState<number>(getNextSuggestedStatusValue());
    const [notes, setNotes] = useState<string>('');
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [locationError, setLocationError] = useState<string>('');
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    // const [message, setMessage] = useState<string>(''); // Replaced by modal state

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const [alertTxHash, setAlertTxHash] = useState<string | undefined>();
    const [pendingRedirect, setPendingRedirect] = useState(false);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatitude(position.coords.latitude);
                    setLongitude(position.coords.longitude);
                    setLocationError('');
                },
                (error) => {
                    console.error("Error getting location:", error);
                    setLocationError(`Error getting location: ${error.message}. Location will be recorded as N/A.`);
                    setLatitude(null);
                    setLongitude(null);
                }
            );
        } else {
            setLocationError("Geolocation is not supported by this browser. Location will be recorded as N/A.");
        }
    }, []);


    const showAlert = (title: string, message: string, type: 'success' | 'error', txHash?: string) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertType(type);
        setAlertTxHash(txHash);
        setAlertOpen(true);
    };

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value.length > MAX_TEXT_LENGTH) {
            showAlert(t('errorTitle'), t('alert.textTooLong', { maxLength: MAX_TEXT_LENGTH }), 'error');
        }
        // Always set the (possibly truncated) value
        setNotes(value.substring(0, MAX_TEXT_LENGTH));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsUpdating(true);

        const latStr = latitude !== null ? latitude.toFixed(5) : "N/A";
        const lonStr = longitude !== null ? longitude.toFixed(5) : "N/A";

        try {
            // Ensure user has a smart account
            await getOrCreateSmartAccount();

            // Update status via backend (sponsored transaction)
            const result = await updateStatusSponsored(tokenId, status, notes, latStr, lonStr);

            if (!result.success) {
                throw new Error(result.error || 'Status update failed');
            }

            showAlert(
                t('pages.statusUpdate.updateSuccessTitle'),
                t('pages.statusUpdate.updateSuccessMessage', { tokenId }),
                'success',
                result.transactionHash
            );
            setPendingRedirect(true);
        } catch (error: any) {
            console.error("Status update failed:", error);
            showAlert(t('pages.statusUpdate.updateFailedTitle'), t('pages.statusUpdate.updateFailedMessage', { error: error.message || 'Failed to update status.' }), 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="status-update-page-container">
            <button onClick={onBack} className="back-button">{t('backToSearch')}</button>
            <div className="certificate-details-header">
                <h3>{t('pages.statusUpdate.titleFor', { name: certificate.name, tokenId })}</h3>
                <div className="certificate-summary">
                    {certificate.image && (
                        <img src={certificate.image} alt={certificate.name} className="certificate-summary-image" />
                    )}
                    <p>{certificate.description}</p>
                </div>
            </div>
            <div className="current-status-display">
                <h4>{t('pages.statusUpdate.currentStatusLabel')}</h4>
                <p>{getCurrentStatus()?.statusName || t('pages.statusUpdate.noStatusHistory')}</p>
            </div>
            <h2>{t('pages.statusUpdate.title')}</h2>
            <div className="contract-link-container">
                <p className="contract-info">
                    {t('pages.statusUpdate.contractInfo')} 
                    <EtherscanLink 
                        type="contract" 
                        hash={process.env.REACT_APP_SUPPLY_CHAIN_TRACKER_ADDRESS || ''}
                    >
                        {t('pages.statusUpdate.viewContract')}
                    </EtherscanLink>
                </p>
            </div>
            {/* Location error is not shown - location is optional */}
            {latitude !== null && longitude !== null && !locationError && (
                <p>{t('pages.statusUpdate.locationLabel', { lat: latitude.toFixed(5), lon: longitude.toFixed(5) })}</p>
            )}
            <form onSubmit={handleSubmit} className="status-update-form">
                {/* <div>
                    <label htmlFor="status-token-id">Token ID:</label>
                    <input type="text" id="status-token-id" value={tokenId} onChange={(e) => tokenId = e.target.value} required />
                </div> */}
                <div>
                    <label htmlFor="item-status">{t('pages.statusUpdate.statusLabel')}</label>
                    <select id="item-status" value={status} onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(parseInt(e.target.value))}>
                        {ITEM_STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(opt.nameKey)}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="status-notes">{t('pages.statusUpdate.notesLabel')}</label>
                    <textarea id="status-notes" value={notes} onChange={handleNotesChange} />
                    <div className="char-counter">{notes.length} / {MAX_TEXT_LENGTH}</div>
                </div>
                <button type="submit" disabled={isUpdating}>
                    {isUpdating ? t('pages.statusUpdate.updatingButton') : t('updateStatusButton')}
                </button>
            </form>
            {/* {message && <p className={message.startsWith("Error") ? "error-message" : "success-message"}>{message}</p>} */}
            <AlertModal
                isOpen={alertOpen}
                onClose={() => {
                    setAlertOpen(false);
                    if (pendingRedirect) {
                        onStatusUpdated(tokenId);
                    }
                }}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                txHash={alertTxHash}
            />
        </div>
    );
};

export default StatusUpdatePage;